import { Context } from "%/dynamic-component-editor/classes/context-v2";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import { ContextValidationError } from "@bloglovin/inspire-engine/Exceptions/ContextValidationError.js";
import { renderSurfaceKind, isCompiledSurfaceKind }
  from "%/dynamic-component-editor/helpers/context-error-report";
import { readRequiredContextsFromRootOptions } from "%/dynamic-component-editor/helpers/story-card-candidates";
import { completionCardScope } from "%/dynamic-component-editor/helpers/completion-card-context-scope";
import {
  completionCardContextGaps,
  describeCompletionCardContextGap,
} from "%/dynamic-component-editor/helpers/completion-card-context-gaps";
import { COMPLETION_CARD_OPTION_BY_STATUS } from "%/dynamic-component-editor/options/completion-card-variant";
import CompletionCardContextBindings from "%/dynamic-component-editor/options/completion-card-context-bindings";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";

const DEFAULT_ELEMENT_STORY_CONTEXT_NAME = "story";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function rawOf(leaf) {
  return isPlainObject(leaf) && ("r" in leaf || "c" in leaf) ? leaf.r ?? leaf.c : leaf;
}

function loadedCard(cardId, isProduction) {
  const component = cardId > 0 ? DynamicComponent.get(cardId, isProduction) : null;
  if (!component?.isHydrated) {
    return null;
  }
  return {
    name: component.name ?? `Component ${cardId}`,
    required: readRequiredContextsFromRootOptions(component.rootOptions) ?? {},
  };
}

export function completionElementContextGaps(element, isProduction = false) {
  const cardIdsByState = {};
  const cardsById = {};
  Object.entries(COMPLETION_CARD_OPTION_BY_STATUS).forEach(([state, optionClass]) => {
    const cardId = Number(rawOf(element.getOptionValue?.(optionClass))) || 0;
    cardIdsByState[state] = cardId;
    const card = loadedCard(cardId, isProduction);
    if (card) {
      cardsById[cardId] = card;
    }
  });
  const bindingsByState = rawOf(element.getOptionValue?.(CompletionCardContextBindings));
  return completionCardContextGaps({
    elementId: element.id ?? element.identifier,
    elementType: element.type,
    cardIdsByState,
    bindingsByState: isPlainObject(bindingsByState) ? bindingsByState : {},
    cardsById,
  });
}

export const CompletionCardContextMixin = {
  repairOnBuilderLoad({ identifier }) {
    return useDynamicComponentEditorStore().repairLegacyCompletionContextBindings(identifier);
  },
  configurationProblems({ element }) {
    if (!element) {
      return [];
    }
    return completionElementContextGaps(element).map(describeCompletionCardContextGap);
  },
  inject: {
    renderingInProduction: { default: false },
    inheritedCompletionScopedContexts: { from: "scopedProvidedContexts", default: null },
  },
  computed: {
    completionCardContextBindings() {
      const raw = rawOf(this.getOptionValue(CompletionCardContextBindings));
      return isPlainObject(raw) ? raw : {};
    },
    completionElementStoryContextName() {
      return this.boundContextName || DEFAULT_ELEMENT_STORY_CONTEXT_NAME;
    },
    resolvedCard() {
      return loadedCard(this.resolvedComponentId, this.renderingInProduction);
    },
    resolvedCardScope() {
      return completionCardScope({
        stateBindings: this.completionCardContextBindings[this.currentStatus],
        required: this.resolvedCard?.required ?? {},
        elementStoryContextName: this.completionElementStoryContextName,
        elementStoryId: Number(this.storyEntityId) || 0,
        lookupSourceContext: name => this.inheritedCompletionScopedContexts?.()?.[name]
          ?? Context.getContext(name)
          ?? null,
      });
    },
    resolvedCardScopedContexts() {
      return this.resolvedCardScope.scoped;
    },
    resolvedCardStoryContextName() {
      return this.resolvedCardScope.storyContextName;
    },
    resolvedCardContextGaps() {
      if (!this.resolvedCard) {
        return [];
      }
      return completionCardContextGaps({
        elementId: String(this.identifier).split(":")[0],
        elementType: this.$options.name,
        cardIdsByState: { [this.currentStatus]: this.resolvedComponentId },
        bindingsByState: this.completionCardContextBindings,
        cardsById: { [this.resolvedComponentId]: this.resolvedCard },
      });
    },
    resolvedCardContextProblemMessages() {
      return this.resolvedCardContextGaps.map(describeCompletionCardContextGap);
    },
    completionCardContextGatePasses() {
      const gaps = this.resolvedCardContextGaps;
      if (!gaps.length) {
        return true;
      }
      if (isCompiledSurfaceKind(renderSurfaceKind()) && !this.editMode && !this.isPreviewMode) {
        throw new ContextValidationError(
          `${this.$options.name} ${gaps[0].elementId} "${gaps[0].state}" card ${gaps[0].cardId} "${gaps[0].cardName}"`,
          gaps.map(describeCompletionCardContextGap),
        );
      }
      return false;
    },
  },
  watch: {
    "resolvedCardScope.unresolved": {
      immediate: true,
      handler(unresolved) {
        if (this.editMode || this.isPreviewMode || !unresolved?.length) {
          return;
        }
        console.warn( // eslint-disable-line no-console
          `${this.$options.name} ${this.identifier}: the "${this.currentStatus}" card ${this.resolvedComponentId} `
          + `binds ${unresolved.map(({ name, source }) => `"${name}" to "${source}"`).join(", ")}, `
          + "but nothing on this page provides that source yet, so the card renders without it.",
          { unresolved, availableScopedContexts: Object.keys(this.inheritedCompletionScopedContexts?.() ?? {}) },
        );
      },
    },
  },
};
